import { defaultCoverUrl } from '../model/defaults';
import type { Seo } from '../model/schemas';

export function createSeo(input: unknown, overrides: Partial<Seo> = {}): Seo {
  const ogImage = getFirstFilledString([
    getValue(input, 'og_image'),
    getValue(input, 'ogImage'),
    getValue(input, 'og.image'),
    getValue(input, 'site_icon'),
    defaultCoverUrl,
  ]);

  return {
    title: getString(input, 'title'),
    description: getString(input, 'description'),
    canonical: getString(input, 'canonical'),
    robots: getString(input, 'robots'),
    og_image: ogImage,
    schema: stringifySchema(getSchema(input)),
    schema_recipe: stringifySchema(
      getFirstFilledString([getValue(input, 'schema_recipe'), getValue(input, 'recipeSchema')]),
    ),
    schema_howto: stringifySchema(getFirstFilledString([getValue(input, 'schema_howto')])),
    schema_faq: stringifySchema(getFirstFilledString([getValue(input, 'schema_faq')])),
    ...overrides,
  };
}

export function createArticleSeo(input: unknown): Seo {
  return createSeo(getValue(input, 'seo'));
}

export function createRecipeSeo(input: unknown): Seo {
  return createSeo(input, {
    schema_recipe: stringifySchema(
      getFirstFilledString([getValue(input, 'recipeSchema'), getValue(input, 'schema_recipe')]),
    ),
  });
}

export function createManualSeo(input: unknown): Seo {
  const schema = getFirstFilledString([getValue(input, 'schema')]);

  return {
    title: getString(input, 'title'),
    description: getString(input, 'description'),
    canonical: getString(input, 'canonical'),
    robots: getString(input, 'robots'),
    og_image: getFirstFilledString([getValue(input, 'image'), defaultCoverUrl]),
    schema,
    schema_recipe: '',
    schema_howto: '',
    schema_faq: '',
  };
}

function getString(input: unknown, path: string): string {
  return stringifySchema(getValue(input, path));
}

function getValue(input: unknown, path: string): unknown {
  if (!input || typeof input !== 'object') return undefined;

  return path.split('.').reduce<unknown>((value, key) => {
    if (!value || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[key];
  }, input);
}

function getFirstFilledString(values: unknown[]): string {
  const value = values.find((item) => stringifySchema(item).trim().length > 0);
  return stringifySchema(value);
}

function getSchema(input: unknown): string {
  return getFirstFilledString([
    getValue(input, 'schema'),
    getValue(input, 'schema_graph'),
    getValue(input, 'schemaGraph'),
  ]);
}

function stringifySchema(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return JSON.stringify(value);
}
